/**
 * LotAllocationMassUploadDialog — bulk insert nhiều dòng Lot Allocation cùng
 * lúc. Pattern tham khảo nút "Mass upload" ở `/futures/orders`
 * (FuturesOrdersListView): grid editable + paste TSV từ Excel + 1 POST bulk.
 * Layout / format / vị trí nút / text bám 1:1 canonical Futures Orders mass
 * upload (MUI Table + actions trong DialogTitle); CHỈ khác cột & rule validate.
 *
 * Cột (theo field bảng `lot_allocation`): Lot ID, Stack ID, GRN ID (optional),
 * Bag Allocated, Kg Allocated.
 *
 * Combo (giống dialog đơn `LotAllocationFormDialog`): Lot ID / Stack ID / GRN ID
 * là Autocomplete RESTRICTED — chỉ chọn được giá trị trong RowSource, KHÔNG nhập
 * tự do giá trị ngoài list. GRN ID keyed theo Stack của chính dòng đó (Access
 * `stack_id_AfterUpdate`); đổi Stack → tự AUTO-PICK GRN đầu tiên của stack (chưa
 * chọn). Paste TSV cũng chỉ giữ giá trị khớp list, giá trị lạ bị bỏ.
 *
 * Bag Allocated / Kg Allocated: nhập tay nhưng CHỈ ký tự số (chặn chữ), blur →
 * format Standard `#,##0.00`; auto-fill từ Pending của (Stack→GRN) qua snapshot
 * (gõ đè được). Nhiều dòng cùng GRN → cross-check cộng dồn chặn vượt Pending.
 *
 * Cross-check (all-or-nothing): auto khi Upload gọi BE dry-run
 * `…/lot-allocation-mass/validate` — validate CỘNG DỒN pending pool theo (lot) +
 * (grn|stack) khi nhiều dòng cùng key chia chung quota; verdict per dòng →
 * highlight đỏ + ✓/✗ + tooltip "Row N: … exceeds … (remaining/requested)". Còn
 * dòng lỗi → Upload disabled. Upload sạch → BE re-validate (FOR UPDATE, race-safe)
 * + insert trong 1 transaction (lỗi 1 dòng → rollback hết).
 */
import AddIcon from '@mui/icons-material/Add'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresSaleContractDialogContentSx,
  futuresSaleContractDialogPaperProps,
  futuresSaleContractDialogTitleSx,
} from '@/pages/futures/futuresAccessUi'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useBulkCreateLotAllocationMutation,
  useGetLotAllocationDoOptionsQuery,
  useGetLotAllocationGrnOptionsQuery,
  useGetLotAllocationOptionsQuery,
  useGetLotAllocationSnapshotQuery,
  useValidateLotAllocationMassMutation,
  type FactoryCell,
} from '@/store/api/factoryApi'

/** Verdict 1 dòng sau cross-check (map theo index dòng gốc trong grid). */
interface RowVerdict {
  ok: boolean
  messages: string[]
  badFields: Set<string>
  /** Gợi ý sửa dòng lỗi (vd "Suggested max here: Bag ≤ …, Kg ≤ …"). */
  suggestion?: string
}

interface MassRow {
  lot_id: string
  stack_id: string
  grn_id: string
  do_no: string
  bag_allocated: string
  kg_allocated: string
}

type FieldKey = keyof MassRow
type FieldKind = 'lot' | 'stack' | 'grn' | 'do' | 'number'

const FIELDS: Array<{ key: FieldKey; label: string; kind: FieldKind; required: boolean; width: number }> = [
  { key: 'lot_id', label: 'Lot ID', kind: 'lot', required: true, width: 160 },
  { key: 'stack_id', label: 'Stack ID', kind: 'stack', required: true, width: 160 },
  { key: 'grn_id', label: 'GRN ID', kind: 'grn', required: false, width: 160 },
  { key: 'do_no', label: 'DO No', kind: 'do', required: false, width: 160 },
  { key: 'bag_allocated', label: 'Bag Allocated', kind: 'number', required: true, width: 140 },
  { key: 'kg_allocated', label: 'Kg Allocated', kind: 'number', required: true, width: 140 },
]

/** Số combo restricted đứng trước nhóm cột số (lot, stack, grn, do). Dùng để
 *  cắt FIELDS giữa combo vs number cell. */
const COMBO_COUNT = 4

// Compact look for the mass-upload table (1:1 với Futures Orders mass upload).
const massCellSx = { px: 0.5, py: 0.25 }
const massInputSx = {
  '& .MuiInputBase-root': { fontSize: '0.72rem', py: 0 },
  '& .MuiInputBase-input': { py: '4px', fontSize: '0.72rem' },
  '& .MuiAutocomplete-input': { py: '2px !important', fontSize: '0.72rem' },
}
const massHeaderSx = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  fontSize: '0.7rem',
  lineHeight: 1.2,
  py: 0.5,
  px: 0.75,
}

const emptyRow = (): MassRow => ({
  lot_id: '',
  stack_id: '',
  grn_id: '',
  do_no: '',
  bag_allocated: '',
  kg_allocated: '',
})

/** Strip thousand-sep + space → numeric string ('' nếu không parse được). */
function normNumber(raw: string): string {
  const cleaned = raw.replace(/,/g, '').replace(/\s+/g, '').trim()
  if (cleaned === '') return ''
  const n = Number(cleaned)
  return Number.isFinite(n) ? String(n) : ''
}

/** Access Format Standard `#,##0.00` — '' cho null/undefined (clear cell). */
function formatStandard(v: number | null | undefined): string {
  if (v === null || v === undefined) return ''
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Khớp `raw` với một option (trim + case-insensitive) → trả về option chuẩn,
 *  '' nếu không khớp. Dùng để chặn giá trị paste ngoài RowSource. */
function canonicalOption(options: string[], raw: string): string {
  const v = (raw ?? '').replace(/\r$/, '').trim()
  if (!v) return ''
  const hit = options.find((o) => o.toLowerCase() === v.toLowerCase())
  return hit ?? ''
}

/** "a\tb\nc\td" → [['a','b'],['c','d']]. */
function parseTsv(text: string): string[][] {
  const trimmed = text.replace(/\r/g, '').replace(/\n$/, '')
  if (!trimmed) return []
  return trimmed.split('\n').map((line) => line.split('\t'))
}

function toNum(v: string): number | null {
  const s = v.replace(/,/g, '').trim()
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

/** Client-side per-row check mirroring the single form's Save gate
 *  (`LotAllocationFormDialog` `canSave`) for the required-field rules the BE
 *  dry-run may NOT enforce: Lot ID / Stack ID / Bag Allocated / Kg Allocated
 *  must be present. Derived from `FIELDS.required` so it stays in sync with the
 *  grid and skips fields not present here. In-list (restricted combo) and
 *  Bag/Kg ≤ Pending are already covered (combo coercion + BE pool) so they are
 *  NOT re-checked. The single form requires bag/kg to be a number (non-null),
 *  not strictly > 0 — mirror presence only, no extra > 0 rule. */
function clientRowCheck(row: MassRow): { ok: boolean; messages: string[]; badFields: Set<string> } {
  const messages: string[] = []
  const badFields = new Set<string>()
  FIELDS.forEach((f) => {
    if (!f.required) return
    const present = f.kind === 'number' ? toNum(row[f.key]) != null : row[f.key].trim() !== ''
    if (!present) {
      messages.push(`${f.label} is required`)
      badFields.add(f.key)
    }
  })
  return { ok: messages.length === 0, messages, badFields }
}

/** Pasted row is a header (not data) when ≥2 leading cells match our column
 *  labels (case-insensitive) — so Copy's header row is dropped on paste-back and
 *  an Excel selection that includes headers doesn't create a bogus data row. */
function isMassHeaderRow(cells: string[]): boolean {
  const expected = FIELDS.map((f) => f.label.toLowerCase())
  let matches = 0
  cells.forEach((c, i) => {
    if (i < expected.length && c.trim().toLowerCase() === expected[i]) matches += 1
  })
  return matches >= 2
}

/** Cell → safe TSV token (tabs/newlines would break the grid). */
function cellForCopy(v: string): string {
  return (v ?? '').replace(/[\t\r\n]/g, ' ')
}

interface Props {
  open: boolean
  factoryCode: string
  onClose: () => void
  /** Gọi sau khi upload thành công (parent có thể refetch / đóng). */
  onUploaded?: () => void
}

export function LotAllocationMassUploadDialog({ open, factoryCode, onClose, onUploaded }: Props) {
  const snack = useSnackbar()
  const [rows, setRows] = useState<MassRow[]>([emptyRow()])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // BE (pool/quota) verdict per row index — filled ONLY by the debounced dry-run.
  // The client required-field verdicts are derived instantly via useMemo (below)
  // and are NEVER stored/blanked. Display + Upload gate merge the two layers.
  const [beVerdictByRow, setBeVerdictByRow] = useState<Record<number, RowVerdict>>({})
  // BE result đang stale (có edit chờ debounce) hoặc đang chạy → Upload phải khoá.
  const [pendingCheck, setPendingCheck] = useState(false)
  const [bulkCreate, { isLoading: uploading }] = useBulkCreateLotAllocationMutation()
  const [validateMass] = useValidateLotAllocationMassMutation()
  // Seq guard: bỏ qua response cross-check cũ khi rows đã đổi tiếp.
  const validateSeqRef = useRef(0)

  // Sửa/paste/xoá dòng → BE verdict cũ stale (index có thể lệch) → xoá ngay để
  // không hiện verdict server sai lệch. CLIENT verdict là useMemo nên tự cập nhật
  // tức thì, KHÔNG bao giờ bị blank.
  const clearBeVerdicts = () => setBeVerdictByRow((v) => (Object.keys(v).length ? {} : v))

  // ── Layer 1: instant CLIENT verdict (1:1 Futures `massVerdicts`) ──────────
  // Tính đồng bộ mỗi keystroke/paste từ `rows` qua clientRowCheck (required Lot
  // ID / Stack ID / Bag / Kg; combo đã restricted). Không debounce, không blank —
  // drive ✓/✗ + red-cell + Alert + caption TỨC THÌ.
  const clientVerdicts = useMemo<RowVerdict[]>(() => rows.map((r) => clientRowCheck(r)), [rows])
  const clientAllOk = clientVerdicts.every((v) => v.ok)

  // ── Merge (client ⊕ be) cho hiển thị: ✗ nếu client fail HOẶC (có BE và BE
  // fail); gộp messages/badFields, giữ 💡 suggestion từ BE. ─────────────────
  const mergedVerdicts = useMemo<RowVerdict[]>(
    () =>
      rows.map((_, i) => {
        const c = clientVerdicts[i]
        const be = beVerdictByRow[i]
        if (!be) return c
        return {
          ok: c.ok && be.ok,
          messages: [...c.messages, ...be.messages],
          badFields: new Set<string>([...c.badFields, ...be.badFields]),
          suggestion: be.suggestion,
        }
      }),
    [rows, clientVerdicts, beVerdictByRow],
  )
  const failCount = mergedVerdicts.filter((v) => !v.ok).length
  const anyError = failCount > 0
  const rowCount = rows.length

  // Combo RowSource (Access Form_Load): lot_id + stack_id. grn_id keyed theo
  // stack của từng dòng → fetch trong RowCells.
  const optionsQuery = useGetLotAllocationOptionsQuery({ factoryCode }, { skip: !open })
  // Dedupe: nguồn có thể trả trùng (vd 1 DO No nhiều dòng) → Autocomplete key theo
  // value sẽ cảnh báo "two children with the same key". Bỏ trùng, giữ thứ tự.
  const uniq = (arr: string[]) => Array.from(new Set(arr))
  const lotIdOptions = uniq(optionsQuery.data?.lot_ids ?? [])
  const stackIdOptions = uniq(optionsQuery.data?.stack_ids ?? [])

  // DO No combo (delivery_order_scs, shared) — fetch 1 lần, dùng chung mọi dòng.
  const doOptionsQuery = useGetLotAllocationDoOptionsQuery({ factoryCode }, { skip: !open })
  const doNoOptions = uniq((doOptionsQuery.data ?? []).map((o) => o.do_no))

  const reset = () => {
    validateSeqRef.current += 1
    setRows([emptyRow()])
    setSelected(new Set())
    setBeVerdictByRow({})
    setPendingCheck(false)
  }

  const handleClose = () => {
    if (uploading) return
    onClose()
  }

  const setCell = (rowIdx: number, field: FieldKey, value: string) => {
    clearBeVerdicts()
    setRows((cur) => cur.map((r, i) => (i === rowIdx ? { ...r, [field]: value } : r)))
  }

  // stack_id đổi → clear grn_id (Access stack_id_AfterUpdate).
  const setStackCell = (rowIdx: number, value: string) => {
    clearBeVerdicts()
    setRows((cur) =>
      cur.map((r, i) =>
        i === rowIdx ? (r.stack_id === value ? r : { ...r, stack_id: value, grn_id: '' }) : r,
      ),
    )
  }

  // Coerce 1 ô paste theo kind cột — dùng chung cho per-cell paste và
  // paste-as-new-row (document Ctrl+V). Combo: chỉ giữ giá trị khớp RowSource
  // (lot/stack/do canonical ngay; grn để RowCells validate khi đã có options theo
  // stack). Bag/Kg: parse → format Standard #,##0.00.
  const coerce = (kind: FieldKind, raw: string): string => {
    if (kind === 'lot') return canonicalOption(lotIdOptions, raw)
    if (kind === 'stack') return canonicalOption(stackIdOptions, raw)
    if (kind === 'do') return canonicalOption(doNoOptions, raw)
    if (kind === 'grn') return (raw ?? '').replace(/\r$/, '').trim()
    const s = normNumber((raw ?? '').replace(/\r$/, ''))
    return s === '' ? '' : formatStandard(Number(s))
  }

  // Paste TSV bắt đầu từ ô (rowIdx, colIdx) — mở rộng grid xuống/sang phải.
  const handlePaste = (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData?.getData('text') ?? ''
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return
    e.preventDefault()
    e.stopPropagation()
    clearBeVerdicts()
    const matrix = parseTsv(text)
    setRows((cur) => {
      const next = cur.map((r) => ({ ...r }))
      matrix.forEach((line, r) => {
        const targetRow = rowIdx + r
        while (next.length <= targetRow) next.push(emptyRow())
        line.forEach((cellRaw, c) => {
          const targetCol = colIdx + c
          const fld = FIELDS[targetCol]
          if (!fld) return
          next[targetRow][fld.key] = coerce(fld.kind, cellRaw)
        })
      })
      return next
    })
  }

  const toggleRow = (idx: number) =>
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })

  const toggleAll = () =>
    setSelected((cur) => (cur.size === rows.length ? new Set() : new Set(rows.map((_, i) => i))))

  const deleteSelected = () => {
    if (selected.size === 0) return
    clearBeVerdicts()
    setRows((cur) => {
      const kept = cur.filter((_, i) => !selected.has(i))
      return kept.length ? kept : [emptyRow()]
    })
    setSelected(new Set())
  }

  // Xoá 1 dòng (nút delete cuối dòng) + reindex selected (giống Futures). Nút bị
  // disable khi chỉ còn 1 dòng nên không cần chèn lại dòng trống.
  const deleteRow = (index: number) => {
    clearBeVerdicts()
    setRows((cur) => cur.filter((_, i) => i !== index))
    setSelected((cur) => {
      const next = new Set<number>()
      cur.forEach((i) => {
        if (i === index) return
        next.add(i > index ? i - 1 : i)
      })
      return next
    })
  }

  // Append 1 khối TSV (từ clipboard hệ thống / Excel, hoặc chính nút Copy) thành
  // các dòng MỚI ở cuối grid; bỏ dòng header nếu có; nếu grid vẫn là dòng starter
  // trống thì thay thế. Trả số dòng thêm.
  const appendMassRowsFromText = (text: string): number => {
    const matrix = parseTsv(text)
    if (matrix.length === 0) return 0
    const dataRows = matrix.length > 1 && isMassHeaderRow(matrix[0]) ? matrix.slice(1) : matrix
    if (dataRows.length === 0) return 0
    const built = dataRows.map((cells) => {
      const row = emptyRow()
      cells.forEach((raw, c) => {
        const fld = FIELDS[c]
        if (!fld) return
        row[fld.key] = coerce(fld.kind, raw)
      })
      return row
    })
    clearBeVerdicts()
    setRows((cur) => {
      const pristine = cur.length === 1 && FIELDS.every((f) => !cur[0][f.key].trim())
      return pristine ? built : [...cur, ...built]
    })
    return built.length
  }

  // Copy dòng đã chọn (hoặc tất cả) ra clipboard — CẢ text/plain (TSV) và
  // text/html, MỖI loại dẫn đầu bằng 1 dòng header nhãn cột. execCommand fallback
  // cho origin HTTP thuần (navigator.clipboard undefined).
  const copyMassRows = async () => {
    const indices = selected.size > 0 ? Array.from(selected).sort((a, b) => a - b) : rows.map((_, i) => i)
    if (indices.length === 0) {
      snack.warning('No rows to copy')
      return
    }
    const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const headerTsv = FIELDS.map((f) => f.label).join('\t')
    const tsv = [
      headerTsv,
      ...indices.map((idx) => FIELDS.map((f) => cellForCopy(rows[idx][f.key])).join('\t')),
    ].join('\n')
    const headerHtml = '<tr>' + FIELDS.map((f) => `<th>${escapeHtml(f.label)}</th>`).join('') + '</tr>'
    const html =
      '<table>' +
      headerHtml +
      indices
        .map(
          (idx) =>
            '<tr>' + FIELDS.map((f) => `<td>${escapeHtml(cellForCopy(rows[idx][f.key]))}</td>`).join('') + '</tr>',
        )
        .join('') +
      '</table>'

    // Selection-based execCommand trên contenteditable div — đặt CẢ text/html +
    // text/plain lên clipboard, chạy trong MUI dialog và trên origin HTTP thuần.
    const copyViaSelection = (): boolean => {
      const div = document.createElement('div')
      div.contentEditable = 'true'
      div.innerHTML = html
      div.style.position = 'fixed'
      div.style.top = '-9999px'
      div.style.left = '-9999px'
      div.style.opacity = '0'
      document.body.appendChild(div)
      const selection = window.getSelection()
      if (!selection) {
        document.body.removeChild(div)
        return false
      }
      const savedRanges: Range[] = []
      for (let i = 0; i < selection.rangeCount; i++) savedRanges.push(selection.getRangeAt(i).cloneRange())
      const range = document.createRange()
      range.selectNodeContents(div)
      selection.removeAllRanges()
      selection.addRange(range)
      let ok = false
      try {
        ok = document.execCommand('copy')
      } catch {
        ok = false
      }
      selection.removeAllRanges()
      savedRanges.forEach((r) => selection.addRange(r))
      document.body.removeChild(div)
      return ok
    }

    const copyViaTextarea = (): boolean => {
      const ta = document.createElement('textarea')
      ta.value = tsv
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.top = '-9999px'
      document.body.appendChild(ta)
      const prevActive = document.activeElement as HTMLElement | null
      ta.focus()
      ta.select()
      let ok = false
      try {
        ok = document.execCommand('copy')
      } catch {
        ok = false
      }
      document.body.removeChild(ta)
      prevActive?.focus?.()
      return ok
    }

    let ok = false
    try {
      const ClipboardItemCtor = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem
      if (ClipboardItemCtor && navigator.clipboard?.write) {
        const item = new ClipboardItemCtor({
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        })
        await navigator.clipboard.write([item])
        ok = true
      }
    } catch {
      ok = false
    }
    if (!ok) ok = copyViaSelection()
    if (!ok) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsv)
          ok = true
        }
      } catch {
        ok = false
      }
    }
    if (!ok) ok = copyViaTextarea()

    if (ok) snack.success(`Copied ${indices.length} row(s) — paste into Excel with Ctrl+V`)
    else snack.error('Browser blocked clipboard access. Open the page over HTTPS or localhost.')
  }

  const errDetail = (err: unknown): string | undefined =>
    typeof err === 'object' && err && 'data' in err
      ? (err as { data?: { detail?: string } }).data?.detail
      : undefined

  // Dòng đã nhập (kèm index gốc trong grid) — bỏ dòng trống hoàn toàn.
  const getFilled = () =>
    rows.map((r, i) => ({ r, i })).filter(({ r }) => FIELDS.some((f) => r[f.key].trim() !== ''))

  const buildPayload = (filled: { r: MassRow }[]): Record<string, FactoryCell | undefined>[] =>
    filled.map(({ r }) => ({
      lot_id: r.lot_id.trim(),
      stack_id: r.stack_id.trim(),
      grn_id: r.grn_id.trim() || undefined,
      do_no: r.do_no.trim() || undefined,
      bag_allocated: toNum(r.bag_allocated),
      kg_allocated: toNum(r.kg_allocated),
    }))

  // BE dry-run → map (pool/quota) verdict theo index dòng gốc. LAYER 2 only:
  // client required-field đã được kiểm tra tức thì (clientVerdicts) ⇒ gọi server
  // chỉ khi client all-ok. Trả true nếu client-ok VÀ mọi dòng BE-ok.
  const runValidate = async (opts?: { quiet?: boolean }): Promise<boolean> => {
    const filled = getFilled()
    if (filled.length === 0) {
      snack.warning('At least 1 row is required')
      return false
    }
    // Client layer (instant) phải sạch trước khi round-trip server.
    if (!clientAllOk) {
      const bad = clientVerdicts.filter((v) => !v.ok).length
      snack.error(`Cross-check: ${bad} row(s) failed — fix highlighted rows`)
      return false
    }
    const seq = ++validateSeqRef.current
    try {
      const res = await validateMass({ factoryCode, rows: buildPayload(filled) }).unwrap()
      const map: Record<number, RowVerdict> = {}
      res.rows.forEach((v, k) => {
        const f = filled[k]
        if (!f) return
        map[f.i] = {
          ok: v.ok,
          messages: v.errors.map((e) => e.message),
          badFields: new Set(v.errors.map((e) => e.field).filter((f): f is NonNullable<typeof f> => !!f)),
          suggestion: v.suggestion?.message,
        }
      })
      if (seq === validateSeqRef.current) setBeVerdictByRow(map)
      const badCount = Object.values(map).filter((v) => !v.ok).length
      if (badCount > 0) snack.error(`Cross-check: ${badCount} row(s) failed — fix highlighted rows`)
      else if (!opts?.quiet) snack.success(`Cross-check passed (${res.rows.length} rows)`)
      return badCount === 0
    } catch (err) {
      snack.error(errDetail(err) || 'Could not run cross-check')
      return false
    }
  }

  // Upload = auto cross-check trước (all-or-nothing). Sạch mới insert.
  const runUpload = async () => {
    const ok = await runValidate({ quiet: true })
    if (!ok) return
    const filled = getFilled()
    try {
      const res = await bulkCreate({ factoryCode, rows: buildPayload(filled) }).unwrap()
      snack.success(`Uploaded ${res.created}/${filled.length} rows`)
      reset()
      onUploaded?.()
      onClose()
    } catch (err) {
      // Lỗi lúc insert (vd race) → surface + cross-check lại để chỉ đúng dòng.
      snack.error(errDetail(err) || 'Could not upload rows')
      void runValidate({ quiet: true })
    }
  }

  // LAYER 2 — debounced BE dry-run (~450ms) cho pool/quota (accumulated Bag/Kg ≤
  // Pending, chỉ server tính được). CHỈ fire khi có ≥1 filled row VÀ client layer
  // KHÔNG còn lỗi (client fail thì ✗ đã hiện tức thì, chưa cần server). Có edit →
  // BE result stale ⇒ pendingCheck=true (Upload khoá); CLIENT verdict KHÔNG blank.
  // Seq guard bỏ response cũ.
  useEffect(() => {
    if (!open) return
    const filled = getFilled()
    if (filled.length === 0 || !clientAllOk) {
      // Không có gì server bổ sung được: chưa có dữ liệu, hoặc client rules còn sai.
      // Giữ client verdict; bỏ BE stale + tắt pending.
      setPendingCheck(false)
      clearBeVerdicts()
      return
    }
    setPendingCheck(true)
    const seq = ++validateSeqRef.current
    const handle = window.setTimeout(async () => {
      try {
        const res = await validateMass({ factoryCode, rows: buildPayload(filled) }).unwrap()
        if (seq !== validateSeqRef.current) return
        const map: Record<number, RowVerdict> = {}
        res.rows.forEach((v, k) => {
          const f = filled[k]
          if (!f) return
          map[f.i] = {
            ok: v.ok,
            messages: v.errors.map((e) => e.message),
            badFields: new Set(
              v.errors.map((e) => e.field).filter((f): f is NonNullable<typeof f> => !!f),
            ),
            suggestion: v.suggestion?.message,
          }
        })
        setBeVerdictByRow(map)
      } catch {
        // Auto path im lặng (tránh spam); Upload vẫn khoá vì thiếu BE verdict ok.
        if (seq !== validateSeqRef.current) return
      } finally {
        if (seq === validateSeqRef.current) setPendingCheck(false)
      }
    }, 450)
    return () => window.clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, open, factoryCode, clientAllOk])

  // Excel paste qua document-level Ctrl+V khi dialog mở: paste KHÔNG nhắm ô editable
  // → append thành dòng mới (bỏ header, coerce field). Ref giữ handler mới nhất để
  // listener ổn định. Per-cell paste vẫn tự xử lý + stopPropagation.
  const appendRef = useRef(appendMassRowsFromText)
  appendRef.current = appendMassRowsFromText
  useEffect(() => {
    if (!open) return
    const onDocPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      const text = e.clipboardData?.getData('text') ?? ''
      if (!text.trim()) return
      e.preventDefault()
      const added = appendRef.current(text)
      if (added > 0) snack.success(`Pasted ${added} new row(s)`)
    }
    document.addEventListener('paste', onDocPaste)
    return () => document.removeEventListener('paste', onDocPaste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Upload gate = client all-ok (instant) AND be all-ok (pool/quota) AND không
  // còn BE stale/pending AND có ≥1 filled row.
  const filledRows = getFilled()
  const beAllOk = filledRows.length > 0 && filledRows.every(({ i }) => beVerdictByRow[i]?.ok)
  const massAllValid = clientAllOk && beAllOk && !pendingCheck && filledRows.length > 0

  const allSelected = rows.length > 0 && selected.size === rows.length

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (uploading || reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      PaperProps={futuresSaleContractDialogPaperProps}
    >
      <DialogTitle sx={futuresSaleContractDialogTitleSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
          <Box component="span">{`Mass upload — Lot Allocation (${factoryCode.toUpperCase()})`}</Box>
          <Box sx={futuresFormDialogActionsBoxSx}>
            <Tooltip title={failCount > 0 ? `${failCount} row(s) failed cross-check — fix to enable Upload` : ''}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  disabled={uploading || filledRows.length === 0 || !massAllValid}
                  onClick={() => void runUpload()}
                  sx={futuresFormDialogActionBtnSx}
                >
                  {uploading ? <CircularProgress size={14} color="inherit" /> : 'Upload'}
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" size="small" onClick={handleClose} sx={futuresFormDialogActionBtnSx}>
              Cancel
            </Button>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
            {/* Cross-check status caption — pending / fail / pass wording aligned to Futures.
                Client layer drives fail/pass instantly; pending = BE dry-run running/stale. */}
            <Box>
              {pendingCheck ? (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, textTransform: 'none !important', letterSpacing: 'normal', color: 'text.secondary' }}
                >
                  Cross-checking…
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, textTransform: 'none !important', letterSpacing: 'normal', color: failCount > 0 ? 'error.main' : 'success.main' }}
                >
                  {failCount > 0
                    ? `Cross-check: ${failCount} of ${rowCount} row(s) need fixing`
                    : `Cross-check passed — ${rowCount} row(s) ready`}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.75}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  clearBeVerdicts()
                  setRows((c) => [...c, emptyRow()])
                }}
                sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
              >
                Add row
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={() => void copyMassRows()}
                sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
              >
                {selected.size > 0 ? `Copy (${selected.size})` : 'Copy all'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                disabled={selected.size === 0}
                onClick={deleteSelected}
                sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
              >
                {selected.size > 0 ? `Delete (${selected.size})` : 'Delete selected'}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={reset}
                sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>

          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', textTransform: 'none !important', letterSpacing: 'normal', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <ContentPasteIcon sx={{ fontSize: 14 }} />
            Copy rows in Excel (or click Copy), then press Ctrl+V to paste them as new rows. You can also paste directly into a cell.
          </Typography>

          <Box sx={{ maxHeight: '55vh', overflow: 'auto', border: '1px solid #bdbdbd', bgcolor: '#fff' }}>
            <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...massHeaderSx, width: 44 }} />
                  <TableCell padding="checkbox" align="left" sx={{ bgcolor: '#fafafa', width: 44, pl: 0.75, pr: 2 }}>
                    <Checkbox
                      size="small"
                      indeterminate={selected.size > 0 && !allSelected}
                      checked={allSelected}
                      onChange={toggleAll}
                      sx={{ p: 0.25, ml: 0 }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...massHeaderSx, width: 36, textAlign: 'center', px: 0.25 }} title="Cross-check status">
                    ✓
                  </TableCell>
                  {FIELDS.map((f) => (
                    <TableCell key={f.key} sx={{ ...massHeaderSx, width: f.width }}>
                      {f.label}
                      {f.required ? ' *' : ''}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIdx) => (
                  <RowCells
                    key={rowIdx}
                    row={row}
                    rowIdx={rowIdx}
                    factoryCode={factoryCode}
                    lotIdOptions={lotIdOptions}
                    stackIdOptions={stackIdOptions}
                    doNoOptions={doNoOptions}
                    selected={selected.has(rowIdx)}
                    verdict={mergedVerdicts[rowIdx]}
                    deleteDisabled={rows.length === 1}
                    onToggle={() => toggleRow(rowIdx)}
                    onCell={(field, value) => setCell(rowIdx, field, value)}
                    onStack={(value) => setStackCell(rowIdx, value)}
                    onPaste={handlePaste}
                    onDelete={() => deleteRow(rowIdx)}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Panel tóm tắt lỗi cross-check + gợi ý — hiện rõ, không cần hover icon. */}
          {anyError && (
            <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.25 } }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, display: 'block', mb: 0.25, textTransform: 'none !important', letterSpacing: 'normal' }}
              >
                Cross-check issues — fix before Upload
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {mergedVerdicts.flatMap((v, idx) => {
                  if (v.ok) return []
                  // 1 dòng / row: ưu tiên gợi ý max từ BE (actionable); nếu chỉ lỗi
                  // required (client) thì gộp message (đã strip prefix "Row N:").
                  const lineText = v.suggestion
                    ? `💡 ${v.suggestion}`
                    : v.messages.map((m) => m.replace(/^Row \d+:\s*/, '')).join('; ')
                  return [
                    <Typography
                      key={idx}
                      component="li"
                      variant="caption"
                      sx={{ display: 'list-item', textTransform: 'none !important', letterSpacing: 'normal' }}
                    >
                      {`Row ${idx + 1}: ${lineText}`}
                    </Typography>,
                  ]
                })}
              </Box>
            </Alert>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

/** Một combo cell restricted: value LUÔN khớp options (không freeSolo → không
 *  commit được giá trị ngoài list). value lệch options → hiển thị rỗng. */
function ComboCell({
  value,
  options,
  loading,
  disabled,
  placeholder,
  onChange,
  onPaste,
}: {
  value: string
  options: string[]
  loading?: boolean
  disabled?: boolean
  placeholder?: string
  onChange: (value: string) => void
  onPaste: (e: React.ClipboardEvent<HTMLElement>) => void
}) {
  // Chỉ feed value cho Autocomplete khi nó nằm trong options (tránh warning
  // "value not in options" + enforce strict list).
  const matched = options.find((o) => o === value) ?? null
  return (
    <Autocomplete
      options={options}
      value={matched}
      onChange={(_, v) => onChange(v ?? '')}
      size="small"
      fullWidth
      disabled={disabled}
      loading={loading}
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      sx={massInputSx}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          onPasteCapture={onPaste}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

function RowCells({
  row,
  rowIdx,
  factoryCode,
  lotIdOptions,
  stackIdOptions,
  doNoOptions,
  selected,
  verdict,
  deleteDisabled,
  onToggle,
  onCell,
  onStack,
  onPaste,
  onDelete,
}: {
  row: MassRow
  rowIdx: number
  factoryCode: string
  lotIdOptions: string[]
  stackIdOptions: string[]
  doNoOptions: string[]
  selected: boolean
  verdict?: RowVerdict
  deleteDisabled: boolean
  onToggle: () => void
  onCell: (field: FieldKey, value: string) => void
  onStack: (value: string) => void
  onPaste: (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => void
  onDelete: () => void
}) {
  const lotId = row.lot_id.trim()
  const stackId = row.stack_id.trim()
  const grnId = row.grn_id.trim()
  // grn_id RowSource keyed theo stack của chính dòng này.
  const grnQuery = useGetLotAllocationGrnOptionsQuery(
    { factoryCode, stackId },
    { skip: !stackId },
  )
  const grnOptions = Array.from(new Set(grnQuery.data ?? []))

  // GRN keyed theo stack: chưa chọn → AUTO-PICK GRN đầu tiên của stack; đã có
  // giá trị (vd vừa paste) nhưng không thuộc list → clear (lượt sau auto-pick lại,
  // enforce "không cho giá trị ngoài list").
  useEffect(() => {
    if (!stackId || grnQuery.isFetching) return
    const g = row.grn_id.trim()
    if (!g) {
      if (grnOptions.length > 0) onCell('grn_id', grnOptions[0])
      return
    }
    if (!grnOptions.some((o) => o.toLowerCase() === g.toLowerCase())) {
      onCell('grn_id', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnQuery.data, grnQuery.isFetching, row.grn_id, stackId])

  // Pending Bag/Kg snapshot (Access lot_id_AfterUpdate + check_number). Fetch
  // khi lot/stack/grn của dòng đổi.
  const snapshotQuery = useGetLotAllocationSnapshotQuery(
    {
      factoryCode,
      lotId: lotId || null,
      stackId: stackId || null,
      grnId: grnId || null,
      allocationId: null,
    },
    { skip: !lotId && !stackId && !grnId },
  )

  // Auto-fill Bag/Kg = Pending phía GRN/Stack khi key (stack|grn) đổi — mirror
  // single-form check_number. Lot đổi không đụng allocation. Track key để KHÔNG
  // ghi đè giá trị user tự nhập sau đó.
  const prevGrnKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (snapshotQuery.isFetching) return
    const data = snapshotQuery.data
    if (!data) return
    const grnKey = `${stackId}|${grnId}`
    if (grnKey === prevGrnKeyRef.current) return
    prevGrnKeyRef.current = grnKey
    onCell('bag_allocated', formatStandard(data.grn_id_bag))
    onCell('kg_allocated', formatStandard(data.grn_id_kg))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotQuery.data, snapshotQuery.isFetching, stackId, grnId])

  // Cross-check verdict → tô nền field vi phạm (nền đỏ nhạt, 1:1 Futures).
  const failed = !!verdict && !verdict.ok
  const cellBg = (field: FieldKey): string | undefined =>
    failed && verdict!.badFields.has(field) ? 'rgba(211,47,47,0.14)' : undefined

  const renderCombo = (
    colIdx: number,
    field: FieldKey,
    options: string[],
    opts: { loading?: boolean; disabled?: boolean; placeholder?: string; onChange: (v: string) => void },
  ) => (
    <TableCell
      sx={{ ...massCellSx, minWidth: FIELDS[colIdx].width, bgcolor: cellBg(field) }}
    >
      <ComboCell
        value={row[field]}
        options={options}
        loading={opts.loading}
        disabled={opts.disabled}
        placeholder={opts.placeholder}
        onChange={opts.onChange}
        onPaste={(e) => onPaste(rowIdx, colIdx, e)}
      />
    </TableCell>
  )

  return (
    <TableRow hover selected={selected}>
      <TableCell sx={{ ...massCellSx, width: 44, verticalAlign: 'middle' }}>
        <Tooltip title="Delete row">
          <span>
            <IconButton size="small" color="error" disabled={deleteDisabled} onClick={onDelete}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell padding="checkbox" align="left" sx={{ width: 44, pl: 0.75, pr: 2 }}>
        <Checkbox size="small" checked={selected} onChange={onToggle} sx={{ p: 0.25, ml: 0 }} />
      </TableCell>
      {/* Cross-check status — ✓ pass / ✗ fail (tooltip liệt kê lý do). */}
      <TableCell sx={{ ...massCellSx, width: 36, textAlign: 'center', verticalAlign: 'middle', px: 0.25 }}>
        {verdict?.ok ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 17, color: 'success.main', verticalAlign: 'middle' }} />
        ) : verdict ? (
          <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{verdict.messages.join('\n')}</Box>}>
            <ErrorOutlineIcon sx={{ fontSize: 17, color: 'error.main', verticalAlign: 'middle' }} />
          </Tooltip>
        ) : null}
      </TableCell>

      {/* Lot ID — combo restricted */}
      {renderCombo(0, 'lot_id', lotIdOptions, { onChange: (v) => onCell('lot_id', v) })}

      {/* Stack ID — combo restricted (đổi → clear GRN) */}
      {renderCombo(1, 'stack_id', stackIdOptions, { onChange: onStack })}

      {/* GRN ID — combo restricted, keyed theo Stack; optional */}
      {renderCombo(2, 'grn_id', grnOptions, {
        loading: grnQuery.isFetching,
        disabled: !stackId,
        placeholder: stackId ? undefined : 'Stack first',
        onChange: (v) => onCell('grn_id', v),
      })}

      {/* DO No — combo restricted (delivery_order_scs); optional */}
      {renderCombo(3, 'do_no', doNoOptions, { onChange: (v) => onCell('do_no', v) })}

      {/* Bag / Kg Allocated — nhập tay nhưng CHỈ ký tự số (chặn chữ): onChange
          lọc còn [0-9 , .]; blur → format Standard #,##0.00. Auto-fill từ Pending
          GRN vẫn chạy (Stack→auto-pick GRN→snapshot), user gõ đè được. */}
      {FIELDS.slice(COMBO_COUNT).map((f, i) => (
        <TableCell key={f.key} sx={{ ...massCellSx, minWidth: f.width, bgcolor: cellBg(f.key) }}>
          <TextField
            value={row[f.key]}
            onChange={(e) => onCell(f.key, e.target.value.replace(/[^\d.,]/g, ''))}
            onBlur={() => {
              const n = toNum(row[f.key])
              onCell(f.key, n == null ? '' : formatStandard(n))
            }}
            onPasteCapture={(e) => onPaste(rowIdx, COMBO_COUNT + i, e)}
            size="small"
            fullWidth
            variant="outlined"
            placeholder="—"
            inputProps={{ style: { textAlign: 'right' }, inputMode: 'decimal' }}
            sx={massInputSx}
          />
        </TableCell>
      ))}
    </TableRow>
  )
}

export default LotAllocationMassUploadDialog
